import React from 'react';
import {
  ResponsiveContainer,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Label
} from 'recharts';
import { StyledSkeletonWrapper, TooltipWrapper } from './style';
import { Co2ReportDataType } from '~/api/services/reporting';
import { formatNumber } from '~/utils/helpers';
import { Nullable } from '~/types';
import { useAuth } from '~/routes/components/AuthProvider';
import { ADMIN_ROLE_SLUG } from '~/Constants';

interface Props {
  data: Nullable<Co2ReportDataType>
  averageTHGLabel: string
  barColors: {
    resultsColor: string,
    dairyResultsColor: string,
    defaultColor: string
  }
  isLoading: boolean
  subSupplierSelected: boolean
  resultsUnit: string
  displayDecimal: boolean
}

const CustomTooltip = ({ active, payload, decimalNumber }: any) => {
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper>
        {payload.map(chartValue => {
          return <div
            key={chartValue.name}
            style={{ color: chartValue.fill || chartValue.stroke, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <div>{chartValue.name}:</div>
            <div style={{ fontWeight: 'bold' }} dangerouslySetInnerHTML={{ __html: `${formatNumber(chartValue.value, decimalNumber)} ${chartValue.unit}` }} />
          </div>
        })}
      </TooltipWrapper>
    );
  }
  return null;
};

const CustomizedAxisTick = ({ x, y, stroke, unit }: any) => {
  const cleanUnit = unit.replace(/<\/?sub>/g, '');
  const [before, after] = cleanUnit.split('2');
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={120} y={0} dy={16} textAnchor='middle' fill='black' fontSize={'15px'} fontWeight={'bold'}>
        {before}
        <tspan dy={5} fontSize='70%'>2</tspan>
        <tspan dy={-5}>{after}</tspan>
      </text>
    </g>)
};

const ResultsChart: React.FC<Props> = (props) => {
  const {
    isLoading,
    data,
    averageTHGLabel,
    barColors: {
      resultsColor,
      defaultColor,
      dairyResultsColor
    },
    displayDecimal
  } = props
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const adminLogged = ADMIN_ROLE_SLUG === roleSlug

  function getLabel(key: string) {
    switch (key) {
      case 'dairyResults':
        return averageTHGLabel
      case 'results':
        return adminLogged ? 'Landwirt(e)' : 'Mein Betrieb'
      case 'defaultResults':
        return 'IDB-Bayern Kuh (LfL)'
      default:
        return ''
    }
  }

  const formattedData = data ? Object.keys(data)?.map(key => {
    if (data[key]?.value) {
      return {
        [key]: data[key]?.value,
        name: getLabel(key)
      }
    }
  }) : []

  const min = data ? Math.min(...Object.values(data).map(result => result?.value).filter(data => !!data)) : 0;
  const max = data ? Math.max(...Object.values(data).map(result => result?.value).filter(data => !!data)) : 1;
  const decimalNumber = displayDecimal ? 2 : 0
  const range = displayDecimal ? 1 : Math.pow(10, max.toString().length > 3 ? 3 : max.toString().length - 1)

  function renderChart() {
    return <BarChart
      data={formattedData.filter(data => !!data)}
      maxBarSize={70}
      margin={{
        top: 50,
        right: 30,
        left: 5,
        bottom: 5
      }}
    >
      <CartesianGrid strokeDasharray='3 3' />
      <XAxis dataKey='name' />
      <YAxis domain={[min, Math.floor(max + range)]} tickFormatter={(value) => formatNumber(value, decimalNumber)}>
        <Label position='top' dx={45} dy={-30} content={<CustomizedAxisTick unit={props.resultsUnit} />} />
      </YAxis>
      <Tooltip content={<CustomTooltip decimalNumber={decimalNumber} />} cursor={false} />
      <Bar
        unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
        dataKey='results'
        stackId={'a'}
        fill={resultsColor}
        stroke={resultsColor}
        name='Mein Betrieb'
        label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
      />

      <Bar
        unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
        dataKey='dairyResults'
        stroke={dairyResultsColor}
        stackId={'a'}
        fill={dairyResultsColor}
        name={averageTHGLabel}
        label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
      />

      <Bar
        unit={props.resultsUnit || 'kg CO<sub>2</sub>-Äq./kg Milch (FPCM)'}
        stackId={'a'}
        fill={defaultColor}
        dataKey='defaultResults'
        stroke={defaultColor}
        name={'IDB-Bayern Kuh (LfL)'}
        label={{ position: 'top', fontSize: 15, fill: '#000', formatter: (value) => formatNumber(value, decimalNumber) }}
      />
    </BarChart>
  }

  function renderSkeleton() {
    return <StyledSkeletonWrapper>
      <div className='skeleton-chart'>
        <div className='skeleton-bar bar1'></div>
        <div className='skeleton-bar bar2'></div>
        <div className='skeleton-bar bar3'></div>
      </div>
    </StyledSkeletonWrapper>
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center' }}>
      <ResponsiveContainer width='70%' height={400}>
        {isLoading ? renderSkeleton() : renderChart()}
      </ResponsiveContainer>
    </div>
  );
};

export default ResultsChart;
